'use client';

import { ButtonHTMLAttributes, forwardRef, ReactNode, useState } from 'react';
import styles from './IconButton.module.css';

type IconButtonVariant = 'standard' | 'filled' | 'tonal' | 'outlined';
type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  toggled?: boolean;
  toggleable?: boolean;
  'aria-label': string;
  children: ReactNode;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  variant = 'standard',
  size = 'md',
  toggled = false,
  toggleable = false,
  disabled = false,
  className = '',
  children,
  onClick,
  ...props
}, ref) => {
  const [isToggled, setIsToggled] = useState(toggled);
  const [ripples, setRipples] = useState<{ x: number; y: number; id: number }[]>([]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    // Ripple
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples(prev => [...prev, { x, y, id }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 600);

    if (toggleable) setIsToggled(prev => !prev);
    onClick?.(e);
  };

  const classNames = [
    styles.iconButton,
    styles[variant],
    styles[size],
    disabled ? styles.disabled : '',
    (toggleable && isToggled) ? styles.toggled : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      className={classNames}
      disabled={disabled}
      onClick={handleClick}
      {...props}
    >
      {ripples.map(r => (
        <span
          key={r.id}
          className={styles.ripple}
          style={{ left: r.x, top: r.y }}
        />
      ))}
      <span className={styles.icon}>{children}</span>
    </button>
  );
});

IconButton.displayName = 'IconButton';
export default IconButton;
